import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { entitiesApi, captableApi } from "@/lib/api";
import { PageShell } from "./Overview";
import { Plus, Trash2, Crown, Zap, RefreshCw, Network, ChevronRight, Building2 } from "lucide-react";
import { toast } from "sonner";

const flagFor = (code) => ({ IN: "🇮🇳", US: "🇺🇸", GB: "🇬🇧", AE: "🇦🇪", SG: "🇸🇬", EE: "🇪🇪", CH: "🇨🇭" }[code] || "");

const OrgChart = () => {
  const [tree, setTree] = useState({ roots: [], count: 0 });
  const [entities, setEntities] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [captable, setCaptable] = useState(null);

  const loadAll = async () => {
    const [t, es] = await Promise.all([entitiesApi.tree(), entitiesApi.list()]);
    setTree(t);
    setEntities(es);
    if (!selectedId && es.length) setSelectedId(es[0].id);
  };

  useEffect(() => { loadAll().catch(() => {}); }, []); // eslint-disable-line
  useEffect(() => {
    if (!selectedId) return;
    captableApi.get(selectedId).then(setCaptable).catch(() => {});
  }, [selectedId]);

  const changeParent = async (eid, parent_id) => {
    try {
      await entitiesApi.patch(eid, { parent_id: parent_id || "" });
      toast.success("Hierarchy updated");
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update");
    }
  };

  // Flatten tree into [{node, depth}, ...] for linear rendering
  const flat = [];
  const walk = (nodes, depth) => {
    for (const n of nodes || []) {
      flat.push({ node: n, depth });
      walk(n.children, depth + 1);
    }
  };
  walk(tree.roots, 0);

  if (tree.count === 0) {
    return (
      <PageShell title="Org chart" subtitle="Entity hierarchy & cap tables">
        <div className="border border-dashed border-border-subtle p-16 text-center">
          <Network size={36} className="mx-auto text-ink-muted mb-4" strokeWidth={1.25} />
          <div className="h-display text-2xl italic text-ink-muted">No entities yet.</div>
          <p className="mt-2 text-sm text-ink-muted">Incorporate your first company to map out subsidiaries and cap tables here.</p>
          <Link to="/app/register" className="mt-6 inline-flex items-center gap-2 bg-artha-amber text-artha-ink px-5 py-3 text-sm hover:bg-artha-cream transition-colors"
            data-testid="org-register-cta">
            Start incorporation
          </Link>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell title="Org chart" subtitle={`${tree.count} entities · parent/subsidiary view`}>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 border border-border-subtle bg-surface-card p-6 overflow-auto" data-testid="org-tree">
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs font-mono-accent uppercase tracking-[0.22em] text-ink-muted">Hierarchy</div>
            <button onClick={loadAll} data-testid="org-refresh-button"
              className="text-xs font-mono-accent uppercase tracking-[0.22em] text-artha-amber hover:text-artha-cream inline-flex items-center gap-1">
              <RefreshCw size={12} /> Refresh
            </button>
          </div>
          <div className="space-y-2">
            {flat.map(({ node, depth }) => {
              const isSel = selectedId === node.id;
              const otherEntities = entities.filter((e) => e.id !== node.id);
              return (
                <div
                  key={node.id}
                  className={`grid grid-cols-12 gap-3 items-center px-4 py-3 border transition-colors cursor-pointer ${
                    isSel ? "border-artha-amber bg-surface-app" : "border-border-subtle bg-surface-app hover:border-artha-amber/50"
                  }`}
                  style={{ marginLeft: depth * 28 }}
                  onClick={() => setSelectedId(node.id)}
                  data-testid={`tree-node-${node.id}`}
                >
                  <div className="col-span-6 flex items-center gap-3 min-w-0">
                    {depth > 0 && <ChevronRight size={14} className="text-ink-muted shrink-0" />}
                    <Building2 size={16} className={isSel ? "text-artha-amber" : "text-ink-muted"} strokeWidth={1.5} />
                    <div className="min-w-0">
                      <div className={`text-sm truncate ${isSel ? "text-artha-amber" : ""}`}>{node.company_name}</div>
                      <div className="text-[10px] font-mono-accent uppercase tracking-[0.2em] text-ink-muted mt-0.5">
                        {flagFor(node.country)} {node.country_name} · {node.entity_type_name}
                      </div>
                    </div>
                  </div>
                  <div className="col-span-3 text-right text-xs font-mono-accent text-ink-muted">
                    {node.shareholder_count} holder{node.shareholder_count === 1 ? "" : "s"}
                  </div>
                  <div className="col-span-3" onClick={(e) => e.stopPropagation()}>
                    <select
                      value={node.parent_id || ""}
                      onChange={(e) => changeParent(node.id, e.target.value)}
                      data-testid={`tree-parent-${node.id}`}
                      className="w-full bg-transparent border border-border-subtle text-xs px-2 py-1.5 focus:outline-none focus:border-artha-amber"
                    >
                      <option value="">No parent</option>
                      {otherEntities.map((e) => <option key={e.id} value={e.id}>↳ {e.company_name}</option>)}
                    </select>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="lg:col-span-5">
          <CapTablePanel entityId={selectedId} data={captable} reload={() => captableApi.get(selectedId).then(setCaptable)} />
        </div>
      </div>
    </PageShell>
  );
};

const CapTablePanel = ({ entityId, data, reload }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [shares, setShares] = useState(1000);
  const [shareClass, setShareClass] = useState("Ordinary");
  const [isFounder, setIsFounder] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!entityId || !data) return (
    <div className="border border-border-subtle bg-surface-card p-6 h-full">
      <div className="text-sm text-ink-muted">Select an entity to edit its cap table.</div>
    </div>
  );

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim() || !Number(shares)) return;
    setBusy(true);
    try {
      await captableApi.add(entityId, {
        name: name.trim(), email: email.trim() || null,
        shares: Number(shares), share_class: shareClass, is_founder: isFounder,
      });
      setName(""); setEmail(""); setShares(1000); setIsFounder(false);
      toast.success("Shareholder added");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (sid) => {
    await captableApi.remove(entityId, sid);
    toast.success("Removed");
    reload();
  };

  const sync = async () => {
    try {
      const res = await captableApi.syncSri(entityId);
      toast.success(`Synced to Sri — ${res.holders} holder(s), ${res.total_shares.toLocaleString()} shares`);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed");
    }
  };

  return (
    <div className="border border-border-subtle bg-surface-card p-6 space-y-6" data-testid="captable-panel">
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="text-xs font-mono-accent uppercase tracking-[0.22em] text-ink-muted">Cap table</div>
          <div className="h-display text-2xl mt-1 truncate">{data.company_name}</div>
        </div>
        <button onClick={sync} data-testid="captable-sync-sri-button"
          className="inline-flex items-center gap-2 px-3 py-2 border border-artha-amber text-artha-amber hover:bg-artha-amber hover:text-artha-ink text-xs font-mono-accent uppercase tracking-[0.2em] transition-colors">
          <Zap size={12} /> {data.sri_synced ? "Re-sync Sri" : "Sync to Sri"}
        </button>
      </div>

      {data.sri_last_sync && (
        <div className="text-[10px] font-mono-accent uppercase tracking-[0.22em] text-ink-muted">
          Last sync · {new Date(data.sri_last_sync).toLocaleString()}
        </div>
      )}

      <div className="grid grid-cols-2 gap-px bg-border-subtle border border-border-subtle">
        <Mini label="Total shares" value={data.total_shares.toLocaleString()} />
        <Mini label="Shareholders" value={data.shareholders.length} />
      </div>

      {data.shareholders.length > 0 ? (
        <div className="border border-border-subtle divide-y divide-border-subtle">
          {data.shareholders.map((h) => (
            <div key={h.id} className="grid grid-cols-12 gap-3 items-center px-4 py-3" data-testid={`shareholder-${h.id}`}>
              <div className="col-span-6 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  {h.is_founder && <Crown size={12} className="text-artha-amber shrink-0" />}
                  <div className="text-sm truncate">{h.name}</div>
                </div>
                {h.email && <div className="text-xs text-ink-muted truncate">{h.email}</div>}
                <div className="text-[10px] font-mono-accent uppercase tracking-[0.22em] text-ink-muted mt-0.5">{h.share_class}</div>
              </div>
              <div className="col-span-3 text-right font-mono-accent text-sm">{h.shares.toLocaleString()}</div>
              <div className="col-span-2 text-right font-mono-accent text-sm text-artha-amber">{h.percentage}%</div>
              <div className="col-span-1 flex justify-end">
                <button onClick={() => remove(h.id)} data-testid={`shareholder-remove-${h.id}`}
                  className="p-1.5 border border-border-subtle hover:border-[#C94F4F] hover:text-[#C94F4F] transition-colors">
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="border border-dashed border-border-subtle p-6 text-center text-sm text-ink-muted">
          No shareholders yet. Add your first below.
        </div>
      )}

      {/* Stacked bar viz */}
      {data.total_shares > 0 && (
        <div>
          <div className="text-[10px] font-mono-accent uppercase tracking-[0.22em] text-ink-muted mb-2">
            Ownership split
          </div>
          <div className="flex h-3 overflow-hidden border border-border-subtle">
            {data.shareholders.map((h, i) => (
              <div key={h.id} title={`${h.name} · ${h.percentage}%`}
                style={{
                  width: `${h.percentage}%`,
                  background: `hsl(${(i * 47) % 360}deg 70% ${i === 0 ? 55 : 45 + (i * 3) % 20}%)`,
                }} />
            ))}
          </div>
        </div>
      )}

      <form onSubmit={add} className="space-y-3 border-t border-border-subtle pt-5" data-testid="add-shareholder-form">
        <div className="text-xs font-mono-accent uppercase tracking-[0.22em] text-ink-muted">Add shareholder</div>
        <div className="grid grid-cols-2 gap-3">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" required data-testid="sh-input-name"
            className="bg-transparent border border-border-subtle px-3 py-2.5 text-sm focus:outline-none focus:border-artha-amber" />
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (optional)" data-testid="sh-input-email"
            className="bg-transparent border border-border-subtle px-3 py-2.5 text-sm focus:outline-none focus:border-artha-amber" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <input type="number" value={shares} onChange={(e) => setShares(e.target.value)} min={1} required data-testid="sh-input-shares"
            placeholder="Shares"
            className="bg-transparent border border-border-subtle px-3 py-2.5 text-sm focus:outline-none focus:border-artha-amber" />
          <select value={shareClass} onChange={(e) => setShareClass(e.target.value)} data-testid="sh-input-class"
            className="bg-transparent border border-border-subtle px-3 py-2.5 text-sm focus:outline-none focus:border-artha-amber">
            <option value="Ordinary">Ordinary</option>
            <option value="Preferred">Preferred</option>
            <option value="ESOP">ESOP</option>
          </select>
          <label className="flex items-center gap-2 text-xs text-ink-muted font-mono-accent uppercase tracking-[0.2em]">
            <input type="checkbox" checked={isFounder} onChange={(e) => setIsFounder(e.target.checked)} data-testid="sh-input-founder" />
            Founder
          </label>
        </div>
        <button type="submit" disabled={busy} data-testid="sh-add-submit"
          className="w-full bg-artha-amber text-artha-ink py-3 text-sm tracking-tight hover:bg-artha-cream transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
          <Plus size={14} /> {busy ? "Adding…" : "Add shareholder"}
        </button>
      </form>
    </div>
  );
};

const Mini = ({ label, value }) => (
  <div className="bg-surface-card p-4">
    <div className="text-[10px] font-mono-accent uppercase tracking-[0.22em] text-ink-muted">{label}</div>
    <div className="h-display text-3xl mt-1">{value}</div>
  </div>
);

export default OrgChart;
